import { useAuth } from "@/contexts/AuthContext";
import { usePersonas } from "./usePersonas";
import { useContexts } from "./useContexts";

export type PlanTier = "trial" | "basic" | "pro" | "business" | "expired";

export interface PlanBadgeStyles {
  textColor: string;
  bgColor: string;
  borderColor: string;
  dotColor: string;
  classes: string;
}

export interface UseSubscriptionReturn {
  isLoading: boolean;
  tier: PlanTier;
  isTrial: boolean;
  isExpired: boolean;
  isSpecialGrant: boolean;
  daysLeftInTrial: number;
  planName: string;
  badgeStyles: PlanBadgeStyles;
  canCreatePersona: boolean;
  canCreateContext: boolean;
  canAccessInsights: boolean;
  canSendMessage: boolean;
  personasCount: number;
  contextsCount: number;
  maxPersonasAllowed: number | null;
  maxContextsAllowed: number | null;
  hasResourceOverflow: boolean;
  excessPersonasCount: number;
  excessContextsCount: number;
}

export function getPlanBadgeStyles(isTrial: boolean, daysLeftInTrial: number, tier: PlanTier): PlanBadgeStyles {
  if (isTrial) {
    if (daysLeftInTrial >= 5) {
      // 7 a 5 dias: Índigo/Violeta Luminoso
      return {
        textColor: "text-indigo-400",
        bgColor: "bg-indigo-500/15",
        borderColor: "border-indigo-500/30",
        dotColor: "bg-indigo-400",
        classes: "text-indigo-400 bg-indigo-500/15 border-indigo-500/30",
      };
    } else if (daysLeftInTrial >= 2) {
      // 4 a 2 dias: Amarelo / Âmbar Brilhante
      return {
        textColor: "text-amber-300",
        bgColor: "bg-amber-500/15",
        borderColor: "border-amber-500/30",
        dotColor: "bg-amber-400",
        classes: "text-amber-300 bg-amber-500/15 border-amber-500/30",
      };
    } else {
      // 1 a 0 dias: Vermelho / Rose Vibrante
      return {
        textColor: "text-rose-400",
        bgColor: "bg-rose-500/15",
        borderColor: "border-rose-500/30",
        dotColor: "bg-rose-400 animate-pulse",
        classes: "text-rose-400 bg-rose-500/15 border-rose-500/30",
      };
    }
  }

  if (tier === "expired") {
    return {
      textColor: "text-rose-400",
      bgColor: "bg-rose-500/15",
      borderColor: "border-rose-500/30",
      dotColor: "bg-rose-500",
      classes: "text-rose-400 bg-rose-500/15 border-rose-500/30",
    };
  }

  if (tier === "business") {
    return {
      textColor: "text-violet-400",
      bgColor: "bg-violet-500/15",
      borderColor: "border-violet-500/30",
      dotColor: "bg-violet-400",
      classes: "text-violet-400 bg-violet-500/15 border-violet-500/30",
    };
  }

  if (tier === "basic") {
    return {
      textColor: "text-sky-400",
      bgColor: "bg-sky-500/15",
      borderColor: "border-sky-500/30",
      dotColor: "bg-sky-400",
      classes: "text-sky-400 bg-sky-500/15 border-sky-500/30",
    };
  }

  // Default Pro
  return {
    textColor: "text-indigo-400",
    bgColor: "bg-indigo-500/15",
    borderColor: "border-indigo-500/30",
    dotColor: "bg-indigo-400",
    classes: "text-indigo-400 bg-indigo-500/15 border-indigo-500/30",
  };
}

export function useSubscription(): UseSubscriptionReturn {
  const { profile, loading, user } = useAuth();
  const { personas } = usePersonas();
  const { contexts } = useContexts();

  const isLoading = Boolean(loading || (user && !profile));
  const now = new Date();
  const personasCount = personas?.length || 0;
  const contextsCount = contexts?.length || 0;

  // 1. VIP / Concessão manual promocional (Acesso Pro 1 Ano)
  if (profile?.is_special_grant) {
    const isVipExpired = Boolean(profile.subscription_expires_at && new Date(profile.subscription_expires_at) < now);
    const tier: PlanTier = isVipExpired ? "expired" : ((profile.subscription_tier as PlanTier) || "pro");
    const isExpired = tier === "expired";

    return {
      isLoading,
      tier,
      isTrial: false,
      isExpired,
      isSpecialGrant: true,
      daysLeftInTrial: 0,
      planName: isExpired ? "Assinatura Expirada" : "Plano Pro",
      badgeStyles: getPlanBadgeStyles(false, 0, tier),
      canCreatePersona: !isExpired,
      canCreateContext: !isExpired,
      canAccessInsights: !isExpired,
      canSendMessage: !isExpired,
      personasCount,
      contextsCount,
      maxPersonasAllowed: null,
      maxContextsAllowed: null,
      hasResourceOverflow: false,
      excessPersonasCount: 0,
      excessContextsCount: 0,
    };
  }

  // 2. Assinatura Paga Ativa (basic, pro, business)
  if (profile?.subscription_tier === "basic" || profile?.subscription_tier === "pro" || profile?.subscription_tier === "business") {
    const isPaidExpired = Boolean(profile.subscription_expires_at && new Date(profile.subscription_expires_at) < now);
    const tier: PlanTier = isPaidExpired ? "expired" : profile.subscription_tier;
    const isExpired = tier === "expired";
    const maxPersonasAllowed = tier === "basic" ? 3 : null;
    const maxContextsAllowed = tier === "basic" ? 3 : null;
    const hasResourceOverflow = !isExpired && tier === "basic" && (personasCount > 3 || contextsCount > 3);
    const excessPersonasCount = tier === "basic" ? Math.max(0, personasCount - 3) : 0;
    const excessContextsCount = tier === "basic" ? Math.max(0, contextsCount - 3) : 0;

    const planLabels: Record<PlanTier, string> = {
      trial: "Trial",
      basic: "Plano Basic",
      pro: "Plano Pro",
      business: "Plano Business",
      expired: "Assinatura Expirada",
    };

    return {
      isLoading,
      tier,
      isTrial: false,
      isExpired,
      isSpecialGrant: false,
      daysLeftInTrial: 0,
      planName: planLabels[tier] || "Plano Pro",
      badgeStyles: getPlanBadgeStyles(false, 0, tier),
      canCreatePersona: !isExpired && !hasResourceOverflow && (tier !== "basic" || personasCount < 3),
      canCreateContext: !isExpired && !hasResourceOverflow && (tier !== "basic" || contextsCount < 3),
      canAccessInsights: !isExpired && tier !== "basic",
      canSendMessage: !isExpired && !hasResourceOverflow,
      personasCount,
      contextsCount,
      maxPersonasAllowed,
      maxContextsAllowed,
      hasResourceOverflow,
      excessPersonasCount,
      excessContextsCount,
    };
  }

  // 3. Usuário em Trial ou Perfil Ausente / Expirado (Fail-Safe: seguro por padrão)
  let daysLeftInTrial = 0;
  let isExpired = !isLoading;
  let tier: PlanTier = isLoading ? "trial" : "expired";

  if (profile?.trial_ends_at) {
    const diffTime = new Date(profile.trial_ends_at).getTime() - now.getTime();
    daysLeftInTrial = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    
    // Só é trial ativo se o tempo restante for estritamente positivo e a data final for no futuro
    if (diffTime > 0 && new Date(profile.trial_ends_at) > now && profile.subscription_tier !== "expired") {
      isExpired = false;
      tier = "trial";
    } else {
      isExpired = true;
      tier = "expired";
    }
  }

  const isTrial = tier === "trial";

  const planLabels: Record<PlanTier, string> = {
    trial: `Trial (${daysLeftInTrial}d restantes)`,
    basic: "Plano Basic",
    pro: "Plano Pro",
    business: "Plano Business",
    expired: "Trial Expirado",
  };

  const badgeStyles = getPlanBadgeStyles(isTrial, daysLeftInTrial, tier);

  return {
    isLoading,
    tier,
    isTrial,
    isExpired,
    isSpecialGrant: false,
    daysLeftInTrial,
    planName: planLabels[tier] || "Trial Expirado",
    badgeStyles,
    canCreatePersona: !isExpired,
    canCreateContext: !isExpired,
    canAccessInsights: false,
    canSendMessage: !isExpired,
    personasCount,
    contextsCount,
    maxPersonasAllowed: 3,
    maxContextsAllowed: 3,
    hasResourceOverflow: false,
    excessPersonasCount: 0,
    excessContextsCount: 0,
  };
}
